/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { requestBaseURL, requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { type AcesSearchSkuRecordResponse, type SearchSkuRecordResponse } from '../interface';
import { useMonetateDecisionFlag } from '@/features/kibo/api/getKiboDecision';
import { useLocale } from '@/hooks/useLocale';
import { useSegments } from '@/features/header/api/getSegments';
import { type QueryClient, type QueryFunctionContext, useQuery } from '@tanstack/react-query';
import { getAxios } from '@/lib/axios';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { showXMPreviewDate } from '@/utils/showXMPreviewDate';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { type Locale } from '@/types/i18n';
import { type AxiosInstance } from 'axios';
import { parseUrl, sanitizeUrlWithParamList } from '@/utils/urlHelpers';
import { acceptableShelfQueryParams } from '@/constants/queryParams';
import { routePaths } from '@/constants/routePaths';
import { useQueryResultOnSuccess } from '@/hooks/query/useQueryResultOnSuccess';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import {
  extractVehicleQuestions,
  serializeVehicleQuestions,
} from '@/features/header/utils/getVehicleQuestions';
import { useHeaderData } from '@/features/header/api/getHeader';
import type { StoreDetails, VehicleSummary } from '@/types';
import { type ParsedUrlQuery } from 'querystring';
import { useRetryConfigForCategory } from '@/components/RetryConfigContext';
import {
  getExperimentId,
  getSearchExperimentConfig,
  selectSearchExperimentUrl,
  buildSearchExperimentHeaders,
  buildSearchExperimentParams,
} from '@/utils/searchExperiment';
import { validateAndFilterVehicleParams } from '@/features/ymme/utils/validateVehicleParams';
import { useVertexPersonalizationParams } from '../hooks/useVertexPersonalizationParams';
import { getSHA256 } from '@/utils/getSHA256';
import { useExperimentId } from '@/utils/searchExperiment/useExperimentId';
import { type AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { useRouter } from 'next/navigation';
import environmentConfig from '@/config/environment';

const SEARCH_SKU_RECORD_URL = `${requestBaseURL}/external/product-discovery/browse-search/v1/search-product`;
const ACES_SEARCH_SKU_RECORD_URL = `${requestBaseURLGCPExtOrInt}/sls/pd/product-navigation-search/v1/search-product`;
const ES_ACES_SEARCH_SKU_RECORD_URL = `${requestBaseURLGCPExtOrInt}/sls/pd/product-browse-search/v1/search-product`;
const CUSTOMER_TYPE = 'B2C';
const SALES_CHANNEL = 'ECOMM';
const STALE_TIME_5_MINUTES = 60000 * 5;

type ACESSearchSkuRecordDataOptions = {
  searchedKeyword?: string;
  sort?: string;
  storeId?: number;
  year?: number;
  makeId?: number;
  modelId?: number;
  vehicleTypeId?: number;
  recordType?: string;
  vehicleQuestions?: Record<string, string | number> | string;
  ignoreVehicleSpecificProductsCheck?: boolean;
  experimentId?: string;
  visitorId?: string;
  userId?: string;
};

export const getAcesSearchSkuRecordOptions = ({
  query,
  preferredVehicle,
  vehicleQuestions,
  searchText,
  storeDetails,
}: {
  query: ParsedUrlQuery;
  preferredVehicle: VehicleSummary | undefined;
  vehicleQuestions: Record<string, string | number> | string;
  searchText: string;
  storeDetails: StoreDetails | undefined;
}): ACESSearchSkuRecordDataOptions => {
  const sort = typeof query.sort === 'string' ? query.sort : undefined;
  const ignoreVehicleSpecificProductsCheck =
    typeof query.ignoreVehicleSpecificProductsCheck === 'string'
      ? query.ignoreVehicleSpecificProductsCheck === 'true'
      : false;

  const vehicleQuestionsString =
    typeof vehicleQuestions === 'string'
      ? vehicleQuestions
      : serializeVehicleQuestions(vehicleQuestions);

  // Validate vehicle parameters - if any base param is missing, exclude ALL vehicle params
  const validatedVehicleParams = validateAndFilterVehicleParams({
    year: preferredVehicle?.year,
    makeId: preferredVehicle?.makeId,
    modelId: preferredVehicle?.modelId,
    vehicleTypeId: preferredVehicle?.vehicleTypeId,
    vehicleQuestions: vehicleQuestionsString || undefined,
  });

  const storeId = storeDetails?.storeNumber ? Number(storeDetails?.storeNumber) : undefined;

  return {
    sort,
    ignoreVehicleSpecificProductsCheck,
    storeId,
    searchedKeyword: searchText,
    ...validatedVehicleParams,
  };
};

type SearchSkuRecordDataOptions = {
  locale: Locale;
  searchText: string;
  userSegments?: string[];
  vehicleId?: string;
  acesEnabled: boolean;
  acesOptions?: ACESSearchSkuRecordDataOptions;
  experimentId?: string;
  visitorId?: string;
  userId?: string;
};

const onSuccess = (
  data: SearchSkuRecordResponse | AcesSearchSkuRecordResponse,
  routerQuery: ParsedUrlQuery,
  router: AppRouterInstance,
) => {
  if (data.redirectUrl) {
    const { url, query } = parseUrl(data.redirectUrl);
    const sanitizedRedirectPath = sanitizeUrlWithParamList(
      {
        ...query,
        ...(routerQuery.searchText && {
          searchText: routerQuery.searchText,
        }),
        redirectUrl: 'true',
      },
      acceptableShelfQueryParams,
      url,
    );

    void router.replace(sanitizedRedirectPath);
  }
};

const getSearchSkuRecordData = async (
  { queryKey }: QueryFunctionContext<ReturnType<typeof getSearchSkuRecordDataKey>>,
  axiosInstance?: AxiosInstance,
) => {
  const [
    ,
    { locale, searchText, userSegments, vehicleId, acesEnabled, acesOptions, experimentId },
  ] = queryKey;
  const country = getCountryFromLocale(locale);
  const preview = showXMPreviewDate();

  const commonParams = {
    country,
    customerType: CUSTOMER_TYPE,
    salesChannel: SALES_CHANNEL,
    preview,
  };

  if (acesEnabled) {
    const normalizedExperimentId = getExperimentId(experimentId, acesEnabled);
    const searchExpConfig = getSearchExperimentConfig({
      experimentId: normalizedExperimentId,
      experimentEnabled: acesEnabled,
    });

    const params = {
      ...commonParams,
      ...acesOptions,
    };

    const finalURL = selectSearchExperimentUrl(
      searchExpConfig,
      ACES_SEARCH_SKU_RECORD_URL,
      ES_ACES_SEARCH_SKU_RECORD_URL,
    );
    // Hash userId if provided (for both client and server-side calls)
    const hashedUserId = acesOptions?.userId ? await getSHA256(acesOptions.userId) : '';
    const enhancedParams = buildSearchExperimentParams(searchExpConfig, params, {
      visitorId: acesOptions?.visitorId,
      userId: hashedUserId,
    });
    const headers = buildSearchExperimentHeaders(searchExpConfig);

    const { data } = await getAxios(axiosInstance).get<AcesSearchSkuRecordResponse>(finalURL, {
      ...(headers && { headers }),
      params: enhancedParams,
      withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
    });

    return data;
  }

  const { data } = await getAxios(axiosInstance).get<SearchSkuRecordResponse>(
    SEARCH_SKU_RECORD_URL,
    {
      params: {
        ...commonParams,
        searchText,
        vehicleId,
        userSegments: userSegments?.join(','),
      },
    },
  );
  return data;
};

const getSearchSkuRecordDataKey = ({
  locale,
  searchText,
  userSegments,
  vehicleId,
  acesEnabled,
  acesOptions,
  experimentId,
}: SearchSkuRecordDataOptions) =>
  [
    'searchRecordData',
    { searchText, locale, userSegments, vehicleId, acesEnabled, acesOptions, experimentId },
  ] as const;

export const useSearchSkuRecordData = () => {
  const isContentStackSRPEnabled = useMonetateDecisionFlag('cmsSearchResultsPageEnabled');
  const { acesEnabled } = useAcesFlags();
  const experimentId = useExperimentId();
  const locale = useLocale();
  const { data: segments, isSuccess: segmentsSuccess } = useSegments();
  const legacyRouter = useLegacyRouter();
  const router = useRouter();
  const preferredVehicle = usePreferredVehicle();
  const { data: storeDetails } = useStoreDetailsData();
  const { data: headerData } = useHeaderData();
  const vehicleQuestions = extractVehicleQuestions(
    headerData?.vehicleMap?.acesVehicleAttributes?.vehicleQuestionsIds,
    true,
  ) as string;

  const searchText = legacyRouter.query.searchText as string;

  const vertexParams = useVertexPersonalizationParams();
  const acesOptions = {
    ...getAcesSearchSkuRecordOptions({
      query: legacyRouter.query,
      preferredVehicle,
      vehicleQuestions,
      searchText,
      storeDetails,
    }),
    ...(vertexParams.shouldInjectParams && {
      visitorId: vertexParams.visitorId,
      userId: vertexParams.userId,
    }),
  };

  const queryEnabled =
    !!isContentStackSRPEnabled &&
    vertexParams.isMyProfileReady &&
    legacyRouter.pathname === routePaths.searchResult &&
    !!searchText &&
    segmentsSuccess;

  const retryConfig = useRetryConfigForCategory('SEARCH');

  const queryResult = useQuery({
    queryKey: getSearchSkuRecordDataKey({
      locale,
      searchText,
      userSegments: segments?.sort() ?? [],
      vehicleId: preferredVehicle?.catalogVehicleId ?? '0',
      acesEnabled,
      acesOptions,
      ...(acesEnabled && experimentId && { experimentId }),
    }),
    queryFn: getSearchSkuRecordData,
    enabled: queryEnabled,
    staleTime: STALE_TIME_5_MINUTES,
    ...retryConfig,
  });

  useQueryResultOnSuccess(
    queryResult,
    (data) => onSuccess?.(data, legacyRouter.query, router),
    queryEnabled,
  );

  return queryResult;
};

export const prefetchSearchSkuRecordData = ({
  queryClient,
  axiosInstance,
  ...options
}: SearchSkuRecordDataOptions & { queryClient: QueryClient; axiosInstance: AxiosInstance }) => {
  return queryClient.prefetchQuery({
    queryKey: getSearchSkuRecordDataKey(options),
    queryFn: (context: QueryFunctionContext<ReturnType<typeof getSearchSkuRecordDataKey>>) =>
      getSearchSkuRecordData(context, axiosInstance),
  });
};

export const getSearchSkuRecordDataFromCache = ({
  queryClient,
  ...options
}: SearchSkuRecordDataOptions & { queryClient: QueryClient }) => {
  return queryClient.getQueryData<SearchSkuRecordResponse | AcesSearchSkuRecordResponse>(
    getSearchSkuRecordDataKey(options),
  );
};
